import * as React from 'react';
import { Box, Chip, Grid, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AiWorkflowRunDetailDto } from '../../models/dtos/AiDto';
import { AiRunStatusChip, AiUsageStatusChip, formatAiCount, formatAiDate, formatAiDuration, formatAiMs, formatAiUsd, StatLine } from './AiShared';

/**
 * One workflow instance (a run key — the caller's correlation id): totals, cost per member, the agent runs it started
 * (each opens the run with its steps) and every model call, oldest first (each task opens its definition).
 * [compact] leaves out the per-call timeline.
 */
export default function AiWorkflowRunView({ run, compact = false, onNavigate }: {
    run: AiWorkflowRunDetailDto;
    compact?: boolean;
    /** Called before a link navigates (e.g. to close the dialog showing this). */
    onNavigate?: () => void;
}) {
    return (
        <Stack gap={2}>
            <Grid container spacing={2}>
                <Grid item xs={6} md={2}><StatLine label="Cost" value={formatAiUsd(run.costUsd)} /></Grid>
                <Grid item xs={6} md={2}><StatLine label="Calls" value={formatAiCount(run.calls)} /></Grid>
                <Grid item xs={6} md={2}><StatLine label="Errors / blocked" value={`${formatAiCount(run.errors)} / ${formatAiCount(run.blocked)}`} /></Grid>
                <Grid item xs={6} md={2}><StatLine label="Web searches / fetches" value={`${formatAiCount(run.webSearches)} / ${formatAiCount(run.webFetches)}`} /></Grid>
                <Grid item xs={6} md={2}><StatLine label="Tokens in / out" value={`${formatAiCount(run.inputTokens)} / ${formatAiCount(run.outputTokens)}`} /></Grid>
                <Grid item xs={6} md={2}><StatLine label="Duration" value={formatAiDuration(run.firstAt, run.lastAt)} /></Grid>
            </Grid>

            <Box>
                <Typography variant="subtitle2" gutterBottom>Cost by member</Typography>
                <Table size="small">
                    <TableHead><TableRow><TableCell>Member</TableCell><TableCell align="right">Calls</TableCell><TableCell align="right">Errors</TableCell><TableCell align="right">Cost</TableCell></TableRow></TableHead>
                    <TableBody>
                        {run.members.map(m => (
                            <TableRow key={`${m.type}:${m.code}`}>
                                <TableCell>
                                    <Chip size="small" variant="outlined" label={m.type.toLowerCase()} sx={{ mr: 1 }} />
                                    <Link component={RouterLink} onClick={onNavigate}
                                        to={m.type === 'TASK' ? `/ai/tasks/${encodeURIComponent(m.code)}` : `/ai/agents/${encodeURIComponent(m.code)}`}>{m.code}</Link>
                                </TableCell>
                                <TableCell align="right">{formatAiCount(m.calls)}</TableCell>
                                <TableCell align="right">{formatAiCount(m.errors)}</TableCell>
                                <TableCell align="right">{formatAiUsd(m.costUsd)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            {run.agentRuns.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>Agent runs</Typography>
                    <Table size="small">
                        <TableHead><TableRow><TableCell>Run</TableCell><TableCell>Agent</TableCell><TableCell>Status</TableCell><TableCell align="right">Turns</TableCell><TableCell align="right">Cost</TableCell><TableCell>Started</TableCell><TableCell>Duration</TableCell></TableRow></TableHead>
                        <TableBody>
                            {run.agentRuns.map(r => (
                                <TableRow key={r.runId}>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>
                                        <Link component={RouterLink} onClick={onNavigate} to={`/ai/runs/${encodeURIComponent(r.runId)}`}>{r.runId.slice(0, 12)}…</Link>
                                    </TableCell>
                                    <TableCell>{r.agentCode}{r.depth > 0 ? ` (child)` : ''}</TableCell>
                                    <TableCell><AiRunStatusChip status={r.status} /></TableCell>
                                    <TableCell align="right">{r.turns}</TableCell>
                                    <TableCell align="right">{formatAiUsd(r.costUsd)}</TableCell>
                                    <TableCell>{formatAiDate(r.startedAt ?? r.createdAt)}</TableCell>
                                    <TableCell>{formatAiDuration(r.startedAt, r.finishedAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {!compact && (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>Calls ({run.timeline.length})</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Time</TableCell><TableCell>Task / agent</TableCell><TableCell>Model</TableCell><TableCell>Status</TableCell>
                                <TableCell align="right">In</TableCell><TableCell align="right">Out</TableCell><TableCell align="right">Cache read</TableCell>
                                <TableCell align="right">Web</TableCell><TableCell align="right">Latency</TableCell><TableCell align="right">Cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {run.timeline.map(c => (
                                <TableRow key={c.id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatAiDate(c.createdAt)}</TableCell>
                                    <TableCell>
                                        {c.taskCode
                                            ? <Link component={RouterLink} onClick={onNavigate} to={`/ai/tasks/${encodeURIComponent(c.taskCode)}`}>{c.taskCode}</Link>
                                            : c.runId
                                                ? <Link component={RouterLink} onClick={onNavigate} to={`/ai/runs/${encodeURIComponent(c.runId)}`}>{c.agentCode ?? 'agent'} · turn</Link>
                                                : c.agentCode ?? '—'}
                                        {c.isFallback && <Chip size="small" label="fallback" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell>{c.modelKey ?? '—'}</TableCell>
                                    <TableCell>
                                        <Tooltip title={c.error ?? ''}><span><AiUsageStatusChip status={c.status} /></span></Tooltip>
                                    </TableCell>
                                    <TableCell align="right">{formatAiCount(c.inputTokens)}</TableCell>
                                    <TableCell align="right">{formatAiCount(c.outputTokens)}</TableCell>
                                    <TableCell align="right">{formatAiCount(c.cacheReadTokens)}</TableCell>
                                    <TableCell align="right">{(c.webSearches ?? 0) + (c.webFetches ?? 0) > 0 ? `${c.webSearches ?? 0}s / ${c.webFetches ?? 0}f` : '—'}</TableCell>
                                    <TableCell align="right">{formatAiMs(c.latencyMs)}</TableCell>
                                    <TableCell align="right">{formatAiUsd(c.costUsd)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Stack>
    );
}
